import React, { useState, useEffect } from "react";
import { Box, Typography, Card, CardContent, Grid, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PercentIcon from "@mui/icons-material/Percent";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import axios from "../../axios-order";
import BoxWrapper from "../../components/ui/boxWrapper";
import RefreshIcon from "@material-ui/icons/Refresh";
import Chip from '@mui/material/Chip';
import { useHistory } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton, Tooltip } from '@mui/material';

const ComplianceData = {
    tax: [
        { icon: <PercentIcon color="primary" />, title: "GSTR", subtitle: "Goods & Services Tax" },
        { icon: <CreditCardIcon color="primary" />, title: "TDS", subtitle: "Tax Deducted at Source" },
        { icon: <MonetizationOnIcon color="primary" />, title: "TCS", subtitle: "Tax Collected at Source" },
    ],

    hr: [
        { icon: <AccountBalanceIcon color="success" />, title: "PF", subtitle: "Provident Fund" },
        { icon: <AssessmentIcon color="success" />, title: "ESI", subtitle: "Employee State Insurance" },
        { icon: <AccountBalanceWalletIcon color="success" />, title: "PT", subtitle: "Professional Tax" },
    ],
};

const cardStyles = {
    height: 100,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    margin: "auto",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    "&:hover": { boxShadow: 4, transform: "scale(1.05)", transition: "0.2s" },
};

const contentStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    textAlign: "center",
    height: "100%",
    padding: "12px 0",
    boxSizing: "border-box",
};

const returnPeriods = [
    { id: 1, label: "Last 3 Months" },
    { id: 2, label: "Last 6 Months" },
    { id: 3, label: "Last 12 Months" }
];

const idToMonths = {
    1: 3,
    2: 6,
    3: 12
};



const isYearlyFiling = (returnType) => {
    return returnType === "GSTR9" || returnType === "GSTR9C";
};

const ComplianceMonitor = () => {
    const [data, setData] = useState(null);
    const [activeCard, setActiveCard] = useState("GSTR");
    const [loaderInRunReport, setLoaderInRunReport] = useState(false);
    const [lastReportRunDatetime, setLastReportRunDatetime] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [showPenaltyView, setShowPenaltyView] = useState(false);
    const [penaltyData, setPenaltyData] = useState(null);
    const [penaltyLoading, setPenaltyLoading] = useState(false);
    const [showPenaltyTable, setShowPenaltyTable] = useState(false);
    const [selectedPenaltyCard, setSelectedPenaltyCard] = useState(null);
    const [loaderInRefresh, setLoaderInRefresh] = useState(false);
    const [editableTaxPaid, setEditableTaxPaid] = useState({});
    const [isOnlyMain, setIsOnlyMain] = useState(false);
    const [calculatedValues, setCalculatedValues] = useState({});
    const [warningMessage, setWarningMessage] = useState(null);
    const [failedEntities, setFailedEntities] = useState([]);
    const [skippedEntities, setSkippedEntities] = useState([]);
    const [gstin, setGstin] = useState(null);
    const [remarks, setRemarks] = useState([]);
    const [isPenaltyButtonDisabled, setIsPenaltyButtonDisabled] = useState(false);
    const [penaltyErrorMessage, setPenaltyErrorMessage] = useState(null);
    const [availableGSTReturns, setAvailableGSTReturns] = useState([]);
    const [editableTurnover, setEditableTurnover] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [modifiedRows, setModifiedRows] = useState(new Set());

    let history = useHistory();
    const handleBack = () => {
        history.push('/home');
    };

    const extractAvailableGSTReturns = (filingData) => {
        if (!filingData) return [];
        return Object.keys(filingData).filter(key => Array.isArray(filingData[key]));
    };

    const handleTaxPaidChange = (index, value) => {
        if (penaltyData && penaltyData.penalty_details[index]) {
            const delayPeriod = parseInt(penaltyData.penalty_details[index].delay_period) || 0
            if (delayPeriod === 0) {
                return;
            }
        }
        setModifiedRows(prev => new Set(prev).add(index));

        // Always update the editable state
        setEditableTaxPaid(prev => ({
            ...prev,
            [index]: value
        }));

        // Parse the value - treat empty string as 0
        const numValue = value === '' ? 0 : Math.max(0, parseFloat(value) || 0);

        if (penaltyData && penaltyData.penalty_details[index]) {
            const row = penaltyData.penalty_details[index];
            const taxPaid = numValue;
            const delayPeriod = parseInt(row.delay_period) || 0;
            const penaltyPerDay = parseFloat(row.penalty_for_each_day_of_default) || 0;

            // Interest Amount = taxpaid * 18% * no.of days delayed / 365
            const interestAmount = (taxPaid * 0.18 * delayPeriod) / 365;

            // Penalty Exposure = Interest Amount + Penalty for each day of default
            const penaltyExposure = taxPaid === 0 ? 0 : interestAmount + (penaltyPerDay * delayPeriod);

            setCalculatedValues(prev => ({
                ...prev,
                [index]: {
                    interestAmount: interestAmount.toFixed(2),
                    penaltyExposure: penaltyExposure.toFixed(2)
                }
            }));
        }
    };

    const handleSave = () => {
        if (!penaltyData?.penalty_details || penaltyData.penalty_details.length === 0) {
            console.error("Missing required data for save");
            return;
        }

        if (modifiedRows.size === 0) {
            alert('No changes to save');
            return;
        }

        setSaveLoading(true);

        const savePromises = [];

        if (penaltyData.file_type === 'GSTR3B') {
            modifiedRows.forEach(index => {
                const row = penaltyData.penalty_details[index];

                const saveData = {
                    id: row.id,
                    file_type: penaltyData.file_type,
                    tax_paid_turnover: editableTaxPaid[index] !== undefined ? parseFloat(editableTaxPaid[index]) : parseFloat(row.tax_paid),
                    interest_amount: parseFloat(calculatedValues[index]?.interestAmount || row.interest_amount),
                    penalty_exposure: parseFloat(calculatedValues[index]?.penaltyExposure || row.penalty_exposure)
                };

                savePromises.push(
                    axios.post('/save_details', saveData)
                        .then(response => {
                            console.log(`Save Response for ID ${row.id}:`, response.data);
                            setPenaltyData(prev => {
                                const updatedDetails = [...prev.penalty_details];
                                updatedDetails[index] = {
                                    ...updatedDetails[index],
                                    tax_paid: saveData.tax_paid,
                                    interest_amount: saveData.interest_amount,
                                    penalty_exposure: saveData.penalty_exposure
                                };
                                return {
                                    ...prev,
                                    penalty_details: updatedDetails
                                };
                            });
                            return { success: true, id: row.id };
                        })
                        .catch(error => {
                            console.error(`Error saving ID ${row.id}:`, error);
                            return { success: false, id: row.id, error };
                        })
                );
            });
        } else if (penaltyData.file_type === 'GSTR9') {
            console.log("GSTR9 save implementation pending");
            setSaveLoading(false);
            return;
        }

        Promise.all(savePromises)
            .then(results => {
                const allSuccess = results.every(r => r.success);
                if (allSuccess) {
                    alert('Data saved successfully!');
                    // Clear modified rows after successful save
                    setModifiedRows(new Set());
                } else {
                    const failedIds = results.filter(r => !r.success).map(r => r.id);
                    alert(`Some records failed to save: ${failedIds.join(', ')}`);
                }
            })
            .finally(() => {
                setSaveLoading(false);
            });
    };

    const handleTurnoverChange = (index, value) => {
        if (penaltyData && penaltyData.penalty_details[index]) {
            const delayPeriod = parseInt(penaltyData.penalty_details[index].delay_period) || 0;
            if (delayPeriod === 0) {
                return;
            }
        }

        // Always update the editable state
        setEditableTurnover(prev => ({
            ...prev,
            [index]: value
        }));

        // Parse the value - treat empty string as 0
        const numValue = value === '' ? 0 : Math.max(0, parseFloat(value) || 0);

        if (penaltyData && penaltyData.penalty_details[index]) {
            const row = penaltyData.penalty_details[index];
            const turnover = numValue;
            const delayPeriod = parseInt(row.delay_period) || 0;

            // If turnover is 0, set everything to 0
            if (turnover === 0) {
                setCalculatedValues(prev => ({
                    ...prev,
                    [`gstr9_${index}`]: {
                        penaltyPerDay: (0).toFixed(2),
                        totalPenalty: (0).toFixed(2),
                        maximumLateFees: (0).toFixed(2),
                        penaltyExposure: (0).toFixed(2)
                    }
                }));
                return;
            }

            // Determine penalty per day based on turnover
            let penaltyPerDay = 0;
            if (turnover <= 500000000) {
                penaltyPerDay = 200;
            } else {
                penaltyPerDay = 250;
            }

            // Calculate total penalty for delays
            const totalPenalty = penaltyPerDay * delayPeriod;

            // Calculate Maximum Late Fees based on turnover
            let maximumLateFees = 0;
            if (turnover <= 200000000) { // Up to 20 Cr
                maximumLateFees = turnover * 0.0004; // 0.04%
            } else { // Greater than 20 Cr
                maximumLateFees = turnover * 0.005; // 0.5%
            }

            // Penalty Exposure is the smaller value between total penalty and maximum late fees
            const penaltyExposure = Math.min(totalPenalty, maximumLateFees);

            setCalculatedValues(prev => ({
                ...prev,
                [`gstr9_${index}`]: {
                    penaltyPerDay: penaltyPerDay.toFixed(2),
                    totalPenalty: totalPenalty.toFixed(2),
                    maximumLateFees: maximumLateFees.toFixed(2),
                    penaltyExposure: penaltyExposure.toFixed(2)
                }
            }));
        }
    };

    // Update the GSTR9 useEffect to initialize from backend values
    useEffect(() => {
        if (penaltyData && penaltyData.file_type === 'GSTR9' && penaltyErrorMessage == null) {
            console.log("penalty details ==>"+ penaltyData.penalty_details + " "+penaltyErrorMessage);
            const initialCalculations = {};
            penaltyData.penalty_details.forEach((row, index) => {
                // Initialize from backend response values
                initialCalculations[`gstr9_${index}`] = {
                    penaltyPerDay: parseFloat(row.penalty_for_each_day_of_default).toFixed(2),
                    totalPenalty: parseFloat(row.total_penalty_for_delays).toFixed(2),
                    maximumLateFees: parseFloat(row.maximum_late_fees).toFixed(2),
                    penaltyExposure: parseFloat(row.penalty_exposure).toFixed(2)
                };
            });
            setCalculatedValues(initialCalculations);
        }
    }, [penaltyData]);



    useEffect(() => {
        if (penaltyData && penaltyData.file_type === 'GSTR3B' && penaltyErrorMessage == null) {
            const initialCalculations = {};
            penaltyData.penalty_details.forEach((row, index) => {
                // Initialize from backend response values
                initialCalculations[index] = {
                    interestAmount: parseFloat(row.interest_amount).toFixed(2),
                    penaltyExposure: parseFloat(row.penalty_exposure).toFixed(2)
                };
            });
            setCalculatedValues(initialCalculations);
        }
    }, [penaltyData]);

    const [formValues, setFormValues] = useState({
        returnPeriod: "Last 3 Months",
    });

    // Auto-select penalty card based on backend file_type
    useEffect(() => {
        if (showPenaltyTable && penaltyData?.file_type) {
            setSelectedPenaltyCard(penaltyData.file_type);
        }
    }, [showPenaltyTable, penaltyData]);

    useEffect(() => {
        fetchFilingStatus(3);
        //handleRefresh();

        // Load available returns from localStorage if exists
        const storedReturns = localStorage.getItem('available_gst_returns');
        if (storedReturns) {
            setAvailableGSTReturns(JSON.parse(storedReturns));
        }
    }, []);

    const handlePenaltyCalculation = (fileType, summary_id, business_entity_id) => {
        // const business_entity_id = localStorage.getItem("business_entity_id");
        // const summary_id = localStorage.getItem("compliance_summary_id");

        if (!business_entity_id || !summary_id) {
            console.error("Missing required IDs");
            return;
        }
        setSelectedPenaltyCard(fileType);
        setPenaltyLoading(true);
        setShowPenaltyTable(false);
        setPenaltyData(null);
        setPenaltyErrorMessage(null);
        axios
            .post('/compliance_penalty_calculation', {
                business_entity_id,
                summary_id,
                file_type: fileType
            })
            .then(response => {
                console.log("Penalty Calculation Response:", response.data);
                if (response.data.penalty_details && response.data.penalty_details.length > 0) {
                    setPenaltyData(response.data);
                    setPenaltyErrorMessage(null);
                    setShowPenaltyTable(true);
                    const penaltyRecordIds = response.data.penalty_details.map(detail => detail.id);
                    localStorage.setItem("penalty_record_ids", JSON.stringify(penaltyRecordIds));
                    if (response.data.file_type === 'GSTR3B') {
                        const initialTaxPaid = {};
                        response.data.penalty_details.forEach((row, index) => {
                            initialTaxPaid[index] = row.tax_paid;
                        });
                        setEditableTaxPaid(initialTaxPaid);
                    }

                    // Initialize editableTurnover with backend values
                    if (response.data.file_type === 'GSTR9') {
                        const initialTurnover = {};
                        response.data.penalty_details.forEach((row, index) => {
                            initialTurnover[index] = row.turnover;
                        });
                        setEditableTurnover(initialTurnover);
                    }
                } else {
                    // No penalty details means error/message case
                    setPenaltyErrorMessage(response.data.message || 'No penalty data available');
                    setPenaltyData({ file_type: fileType }); // Store file_type for title
                    setShowPenaltyTable(true);
                }
            })
            .catch(error => {
                console.error('Error calculating penalty:', error);
                const errorMsg = error.response?.data?.message || error.message || 'Error calculating penalty';
                setPenaltyErrorMessage(errorMsg);
                setPenaltyData({ file_type: fileType });
                setShowPenaltyTable(true);
            })
            .finally(() => {
                setPenaltyLoading(false);
                setSelectedPenaltyCard(null);
            });
    };



    // Run Report - calls POST API (you can update the endpoint later)
    const handleRunReport = () => {
        const seller_code = localStorage.getItem("business_entity_id");
        const user_id = localStorage.getItem("user_id")
        const selectedPeriod = returnPeriods.find(p => p.label === formValues.returnPeriod);
        const num_months = idToMonths[selectedPeriod.id];

        setLoaderInRunReport(true);

        const requestBody = {
            seller_code: parseInt(seller_code),
            num_months: num_months,
            user_id: parseInt(user_id)
        };

        axios
            .post('/fetch_status_details', requestBody)
            .then(response => {
                console.log("Run Report Response:", response.data);
                const logId = response.data.compliance_summary_id;
                if (logId) {
                    localStorage.setItem("compliance_summary_id", logId);
                }
                console.log("ID:", logId);


                // Only use the data the backend sent
                if (response.data.results && response.data.results.length > 0) {
                    const transformedData = transformFilingData(response.data.results);
                    setData(transformedData);
                    setErrorMessage(null);
                    setIsPenaltyButtonDisabled(false);
                    const entityIds = response.data.results
                        .filter(entity => entity.id)
                        .map(entity => entity.id);

                    if (entityIds.length > 0) {
                        localStorage.setItem("entity_ids", JSON.stringify(entityIds));
                        localStorage.setItem("compliance_summary_id", entityIds[0]);
                    }

                } else {
                    setErrorMessage({
                        text: response.data.message,
                        isSuccess: response.data.status === true
                    });
                    setData([]);
                    setIsPenaltyButtonDisabled(true);
                }
                fetchFilingStatus(num_months);
            })
            .catch(error => {
                console.error('Error running report:', error);
                setErrorMessage(error.response?.data?.message || null);
                setData([]);
                setIsPenaltyButtonDisabled(true);
            })
            .finally(() => {
                setLoaderInRunReport(false);
            });
    };

    const handleRefresh = () => {
        const business_entity_id = localStorage.getItem("business_entity_id");
        const selectedPeriod = returnPeriods.find(p => p.label === formValues.returnPeriod);
        const num_months = idToMonths[selectedPeriod.id];

        // Call fetchFilingStatus to update the table
        fetchFilingStatus(num_months);

        // setLoaderInRefresh(true);

        // // First, call the filing status API to check if ID exists
        // axios
        //     .get(`/get_filing_status_details/${business_entity_id}/${num_months}`)
        //     .then(filingResponse => {
        //         console.log("Filing Status Response:", filingResponse.data);

        //         // Check if results exist and have an ID
        //         if (filingResponse.data.results &&
        //             filingResponse.data.results.length > 0 &&
        //             filingResponse.data.results[0].id) {

        //             const compliance_summary_id = filingResponse.data.results[0].id;
        //             console.log("ID found in filing status:", compliance_summary_id);

        //             // Store the ID in localStorage
        //             localStorage.setItem("compliance_summary_id", compliance_summary_id);

        //             // Now call the refresh API since ID exists
        //             return 
        //         } else {
        //             // No ID found in filing status response
        //             console.log("No ID found in filing status response - skipping refresh API");
        //             setErrorMessage("No compliance summary ID found. Please run the report first.");
        //             setIsPenaltyButtonDisabled(true);

        //             // Still call fetchFilingStatus to show whatever data is available
        //             fetchFilingStatus(num_months);
        //         }
        //     })
        //     .catch(error => {
        //         console.error("Error in refresh flow:", error);
        //         setErrorMessage(error.response?.data?.message || "Error refreshing report");
        //         setIsPenaltyButtonDisabled(true);
        //     })
        //     .finally(() => {
        //         setLoaderInRefresh(false);
        //     });
    };

    const fetchFilingStatus = (num_months) => {
        const seller_code = localStorage.getItem("business_entity_id");
        const user_id = localStorage.getItem("user_id");
        console.log("Seller Code:", seller_code);
        console.log("User ID:", user_id);
        console.log("API Params:", { seller_code, user_id, num_months });

        // Only block if BOTH are missing
        if (!seller_code && !user_id) {
            console.error("Missing both business_entity_id and user_id");
            setErrorMessage(null);
            return;
        }

        const requestBody = {
            user_id: user_id,
            seller_code: seller_code,
            num_months: num_months,
            is_only_main: !!seller_code // true if seller_code exists, false otherwise
        };

        console.log("POST Request Body:", requestBody);
        setErrorMessage(null);
        setLoaderInRefresh(true);
        axios.post('/get_filing_status_details', requestBody)

            .then(response => {
                console.log("Full API Response:", response.data);
                const isOnlyMainValue = requestBody.is_only_main === true || requestBody.is_only_main === 'true';
                setIsOnlyMain(isOnlyMainValue);
                console.log("SET isOnlyMain →", isOnlyMainValue);

                // Bind last_generated_date from the response
                if (response.data.last_generated_date) {
                    setLastReportRunDatetime(response.data.last_generated_date);
                }
                const responseData = response.data.data || {};

                // Check each status in priority order
                if (responseData.success?.length > 0) {
                    // SUCCESS: Valid results available
                    const complianceId = responseData.success[0].id;
                    localStorage.setItem("compliance_summary_id", complianceId);
                    const transformedData = transformFilingData(responseData.success);
                    setData(transformedData);
                    setErrorMessage(null);
                    setIsPenaltyButtonDisabled(false);
                    setFailedEntities(responseData.failed || []);
                    setSkippedEntities(response.data.invalid_gstin_entities || []);
                    setRemarks([]);
                } else if (responseData.processing?.length > 0) {
                    // PROCESSING: Entities are being processed
                    const remarks = responseData.processing.map(entity => ({
                        entity_name: entity.entity_name,
                        gstin: entity.gstin,
                        status: entity.status,
                        remarks: entity.remarks
                    }));

                    setRemarks(remarks);
                    setData([]);
                    setIsPenaltyButtonDisabled(true);
                    setFailedEntities([]);
                    setErrorMessage({
                        text: response.data.remarks || "Currently being processed",
                        isSuccess: response.data.status === true
                    });

                } else if (responseData.initiated?.length > 0) {

                    // INITIATED: Processing has been initiated
                    const remarks = responseData.initiated.map(entity => ({
                        entity_name: entity.entity_name,
                        gstin: entity.gstin,
                        status: entity.status,
                        remarks: entity.remarks
                    }));

                    setRemarks(remarks);
                    setData([]);
                    setIsPenaltyButtonDisabled(true);
                    setFailedEntities([]);
                    setErrorMessage({

                        text: response.data.message || "Processing has been initiated",
                        isSuccess: response.data.status === true
                    });

                } else if (responseData.failed?.length > 0) {

                    setRemarks([]);
                    setFailedEntities(responseData.failed);
                    setData([]);
                    setIsPenaltyButtonDisabled(true);
                    setErrorMessage(null);
                }
                else {
                    // NO DATA: Empty response
                    setRemarks([]);
                    setData([]);
                    setIsPenaltyButtonDisabled(true);
                    setFailedEntities([]);
                    setErrorMessage({
                        text: response.data.message || "No data available",
                        isSuccess: response.data.status === true
                    });
                }
            })
            .catch(error => {
                console.error('Error fetching filing status:', error);
                setErrorMessage(error.response?.data?.message || null);
                setData([]);
                setIsPenaltyButtonDisabled(true);
                setFailedEntities([]);
            })
            .finally(() => {
                setLoaderInRefresh(false);
            });

    }


    const transformFilingData = (results) => {
        if (!results || results.length === 0) return [];

        // Collect all unique GST return types from ALL entities
        const allReturnsSet = new Set();
        results.forEach(entity => {
            if (entity.filing_data) {
                const returnsForEntity = extractAvailableGSTReturns(entity.filing_data);
                returnsForEntity.forEach(returnType => allReturnsSet.add(returnType));
            }
        });

        const allAvailableReturns = Array.from(allReturnsSet);
        console.log("360 ------->", allAvailableReturns);

        if (allAvailableReturns.length > 0) {
            setAvailableGSTReturns(allAvailableReturns);
            localStorage.setItem('available_gst_returns', JSON.stringify(allAvailableReturns));
        }

        return results.map(entity => {
            const filingData = entity.filing_data || {};
            const filingSummary = [];

            // Transform each return type (GSTR1, GSTR3B, etc.)
            Object.keys(filingData).forEach(returnType => {
                const periods = filingData[returnType] || [];
                const summaryRow = { Return_Type: returnType };

                periods.forEach(period => {
                    const monthKey = period.return_period; // e.g., "Apr-23"
                    summaryRow[monthKey] = {
                        date: period.date_of_filing,
                        isDelayed: period.is_delay_file === "True"
                    };
                });

                filingSummary.push(summaryRow);
            });

            return {
                ...entity,
                filing_status_details: {
                    Filing_Summary: filingSummary
                }
            };
        });
    };

    const handleCardClick = (title) => {
        setActiveCard(title);
        console.log('Card clicked:', title);
    };

    const handleReturnPeriodChange = (event) => {
        const selectedLabel = event.target.value;
        const selectedPeriod = returnPeriods.find(p => p.label === selectedLabel);
        if (!selectedPeriod) return;

        const num_months = idToMonths[selectedPeriod.id];

        setFormValues(prev => ({
            ...prev,
            returnPeriod: selectedLabel,
        }));

        console.log("Selected:", selectedLabel, "→ num_months:", num_months);
        fetchFilingStatus(num_months);
    };

    const filingSummary = data?.[0]?.filing_status_details?.Filing_Summary || [];
    const filteredFilingSummary = filingSummary.filter(filing =>
        activeCard === "GSTR" ? filing.Return_Type.includes("GSTR") : filing.Return_Type.includes(activeCard)
    ) || [];

    const yearlyFilings = filteredFilingSummary.filter(filing => isYearlyFiling(filing.Return_Type));
    const monthlyFilings = filteredFilingSummary.filter(filing => !isYearlyFiling(filing.Return_Type));

    // Get the maximum number of months across all monthly filings to determine row count
    let maxMonthsCount = 0;
    monthlyFilings.forEach(filing => {
        const monthCount = Object.keys(filing).filter(key => key !== "Return_Type").length;
        if (monthCount > maxMonthsCount) {
            maxMonthsCount = monthCount;
        }
    });

    // Calculate how many rows we need (3 months per row)
    const numRows = Math.ceil(maxMonthsCount / 3);

    const leftColumnsCount = 1 + yearlyFilings.length;
    return (
        <Box>
            {!showPenaltyView ? (
                <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", }}>
                    <Box sx={{ flex: 1, p: { xs: 1.5, md: 0 } }}>
                        <Box sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            mb: 2,
                            pl: { xs: 1, md: 2 }
                        }}>
                            {isOnlyMain === false && (
                                <Tooltip title="Back" arrow>
                                    <IconButton
                                        onClick={handleBack}
                                        sx={{
                                            borderRadius: '50%',
                                            padding: '6px',
                                            color: '#263238',
                                            fontSize: '20px',
                                            backgroundColor: '#ffffff',
                                            height: '36px',
                                            width: '36px',
                                            marginRight: '12px',
                                            '&:hover': {
                                                backgroundColor: '#455a64',
                                                color: '#ffffff',
                                            },
                                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                                            transition: 'background-color 0.3s ease',
                                        }}
                                    >
                                        <ArrowBackIcon sx={{ fontSize: '20px' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <button
                                className="button-primary"
                                onClick={handleRunReport}
                                disabled={loaderInRunReport}
                                style={{ padding: "8px 15px" }}
                            >
                                {loaderInRunReport ? "Running..." : "Run Report"}
                            </button>
                        </Box>
                        <Grid container spacing={3} alignItems="center" justifyContent="center" sx={{ mt: -4 }}>
                            {/* TAX COMPLIANCES */}
                            <Grid item xs={12} md={5}>
                                <Box mb={3} textAlign="center">
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <MonetizationOnIcon sx={{ fontSize: 20, color: "primary.main", mr: 0.5 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontFamily: '"Dosis", sans-serif', letterSpacing: '1px' }}>
                                            Tax Compliances
                                        </Typography>
                                    </Box>
                                </Box>
                                <Grid container spacing={2} justifyContent="center">
                                    {ComplianceData.tax.map((item, idx) => (
                                        <Grid item xs={4} key={idx}>
                                            <Card
                                                onClick={item.title === "GSTR" ? () => handleCardClick(item.title) : undefined}
                                                sx={{
                                                    ...cardStyles,
                                                    backgroundColor: activeCard === item.title ? "#1976d2" : "#fff",
                                                    color: activeCard === item.title ? "#fff" : "#000",
                                                    transition: "background-color 0.3s ease, color 0.3s ease",
                                                    pointerEvents: item.title !== "GSTR" ? "none" : "auto",
                                                    cursor: item.title !== "GSTR" ? "not-allowed" : "pointer"
                                                }}
                                            >
                                                <CardContent sx={contentStyles}>
                                                    {React.cloneElement(item.icon, {
                                                        sx: {
                                                            fontSize: 26,
                                                            color: activeCard === item.title ? "#fff" : "primary.main",
                                                            mb: 0.5
                                                        }
                                                    })}
                                                    <Typography
                                                        variant="subtitle2"
                                                        fontWeight="bold"
                                                        sx={{
                                                            fontSize: 15,
                                                            color: activeCard === item.title ? "#fff" : "primary.main",
                                                            fontFamily: '"Dosis", sans-serif',
                                                            letterSpacing: '1px'
                                                        }}
                                                    >
                                                        {item.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: 9,
                                                            fontFamily: '"Dosis", sans-serif',
                                                            letterSpacing: '1px',
                                                            color: activeCard === item.title ? "#fff" : "text.secondary"
                                                        }}
                                                    >
                                                        {item.subtitle}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* HR COMPLIANCES */}
                            <Grid item xs={12} md={5}>
                                <Box mb={3} textAlign="center">
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <PersonIcon sx={{ fontSize: 20, color: "success.main", mr: 0.5 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontFamily: '"Dosis", sans-serif', letterSpacing: '1px' }}>
                                            HR Compliances
                                        </Typography>
                                    </Box>
                                </Box>
                                <Grid container spacing={2} justifyContent="center">
                                    {ComplianceData.hr.map((item, idx) => (
                                        <Grid item xs={4} key={idx}>
                                            <Card
                                                onClick={item.title === "GSTR" ? () => handleCardClick(item.title) : undefined}
                                                sx={{
                                                    ...cardStyles,
                                                    backgroundColor: activeCard === item.title ? "#2e7d32" : "#fff",
                                                    color: activeCard === item.title ? "#fff" : "#000",
                                                    transition: "background-color 0.3s ease, color 0.3s ease",
                                                    pointerEvents: item.title !== "GSTR" ? "none" : "auto",
                                                    cursor: item.title !== "GSTR" ? "not-allowed" : "pointer"
                                                }}
                                            >
                                                <CardContent sx={contentStyles}>
                                                    {React.cloneElement(item.icon, {
                                                        sx: {
                                                            fontSize: 26,
                                                            color: activeCard === item.title ? "#fff" : "success.main",
                                                            mb: 0.5
                                                        }
                                                    })}
                                                    <Typography
                                                        variant="subtitle2"
                                                        fontWeight="bold"
                                                        sx={{
                                                            fontSize: 15,
                                                            color: activeCard === item.title ? "#fff" : "success.main",
                                                            fontFamily: '"Dosis", sans-serif',
                                                            letterSpacing: '1px'
                                                        }}
                                                    >
                                                        {item.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: 9,
                                                            fontFamily: '"Dosis", sans-serif',
                                                            letterSpacing: '1px',
                                                            color: activeCard === item.title ? "#fff" : "text.secondary"
                                                        }}
                                                    >
                                                        {item.subtitle}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* RETURN PERIOD FILTER AND FILING STATUS TABLE */}
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    gap: 2,
                                    pr: { xs: 2, md: 10 }
                                }}>

                                    {/* Last Report Run Date Time */}
                                    {/* {lastReportRunDatetime && (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <p style={{ marginRight: '10px', marginBottom: '0' }}>
                                                LAST REPORT RUN DATE TIME:
                                            </p>
                                            <Chip
                                                label={lastReportRunDatetime}
                                                variant="outlined"
                                                color="primary"
                                                size="medium"
                                            />
                                        </div>
                                    )} */}

                                    {/* Filter dropdown */}
                                    <FormControl
                                        variant="outlined"
                                        size="small"
                                        sx={{ width: "220px" }}
                                    >
                                        <InputLabel id="return-period-label">Filter by Return Period</InputLabel>
                                        <Select
                                            labelId="return-period-label"
                                            id="return-period"
                                            value={formValues.returnPeriod}
                                            onChange={handleReturnPeriodChange}
                                            label="Filter by Return Period"
                                        >
                                            {returnPeriods.map((period) => (
                                                <MenuItem key={period.id} value={period.label}>
                                                    {period.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* 3. Refresh button (icon only) */}
                                    <button
                                        className="button-outlined-primary"
                                        onClick={handleRefresh}
                                        style={{ padding: "8px 12px", minWidth: "auto" }}
                                    >
                                        <RefreshIcon style={{ fontSize: "20px" }} />
                                    </button>
                                </Box>
                            </Grid>


                            <Grid item xs={isOnlyMain === false ? 11.5 : 12}>
                                {loaderInRefresh ? (
                                    // Show loading state when refresh is in progress
                                    <BoxWrapper
                                        boxTitle={
                                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                    sx={{
                                                        padding: '4px 20px 4px 0px',
                                                        fontSize: '18px',
                                                        fontFamily: 'Dosis',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    Filing Status
                                                </Typography>
                                                {lastReportRunDatetime && (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '4px 20px 4px 0px'
                                                    }}>
                                                        <p style={{
                                                            marginRight: '10px',
                                                            marginBottom: '0',
                                                            fontSize: '18px',
                                                            fontWeight: 500,
                                                            color: 'black',
                                                            fontFamily: 'Dosis'
                                                        }}>
                                                            Last Report Run Date Time:
                                                        </p>
                                                        <Chip
                                                            label={lastReportRunDatetime}
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            sx={{
                                                                fontSize: '14px',
                                                                height: '32px',
                                                                '& .MuiChip-label': {
                                                                    padding: '0 14px'
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </Box>
                                        }
                                        enableAddButton={false}
                                    >
                                        <div style={{ overflowX: 'auto' }}>
                                            <table className="custome-table" style={{
                                                borderCollapse: 'collapse',
                                                border: '1px solid #ddd',
                                                width: '100%',
                                                minWidth: '800px'
                                            }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                        <th colSpan={4} style={{
                                                            padding: '40px 20px',
                                                            textAlign: 'center',
                                                            border: '1px solid #ddd',
                                                            color: '#666',
                                                            fontSize: '1em',
                                                            fontWeight: '600'
                                                        }}>
                                                            Loading data...
                                                        </th>
                                                    </tr>
                                                </thead>
                                            </table>
                                        </div>
                                    </BoxWrapper>
                                ) : data && data.length > 0 ? (
                                    data.map((entity, entityIndex) => {
                                        // Calculate filtered summary for this specific entity
                                        const entityFilingSummary = entity.filing_status_details?.Filing_Summary || [];
                                        const entityFilteredFilingSummary = entityFilingSummary.filter(filing =>
                                            activeCard === "GSTR" ? filing.Return_Type.includes("GSTR") : filing.Return_Type.includes(activeCard)
                                        ) || [];

                                        const entityYearlyFilings = entityFilteredFilingSummary.filter(filing => isYearlyFiling(filing.Return_Type));
                                        const entityMonthlyFilings = entityFilteredFilingSummary.filter(filing => !isYearlyFiling(filing.Return_Type));

                                        let entityMaxMonthsCount = 0;
                                        entityMonthlyFilings.forEach(filing => {
                                            const monthCount = Object.keys(filing).filter(key => key !== "Return_Type").length;
                                            if (monthCount > entityMaxMonthsCount) {
                                                entityMaxMonthsCount = monthCount;
                                            }
                                        });

                                        const entityNumRows = Math.ceil(entityMaxMonthsCount / 3);
                                        const entityLeftColumnsCount = 1 + entityYearlyFilings.length;

                                        return (
                                            <React.Fragment key={entity.id || entityIndex}>
                                                <BoxWrapper
                                                    boxTitle={
                                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                            <Typography
                                                                variant="h6"
                                                                component="div"
                                                                sx={{
                                                                    padding: '4px 20px 4px 0px',
                                                                    fontSize: '18px',
                                                                    fontFamily: 'Dosis',
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                Filing Status
                                                            </Typography>
                                                            {lastReportRunDatetime && (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '4px 20px 4px 0px'
                                                                }}>
                                                                    <p style={{
                                                                        marginRight: '10px',
                                                                        marginBottom: '0',
                                                                        fontSize: '18px',
                                                                        fontWeight: 500,
                                                                        color: 'black',
                                                                        fontFamily: 'Dosis'
                                                                    }}>
                                                                        Last Report Run Date Time:
                                                                    </p>
                                                                    <Chip
                                                                        label={lastReportRunDatetime}
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        size="small"
                                                                        sx={{
                                                                            fontSize: '14px',
                                                                            height: '32px',
                                                                            '& .MuiChip-label': {
                                                                                padding: '0 14px'
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </Box>
                                                    }
                                                    enableAddButton={false}
                                                >
                                                    <div style={{ overflowX: 'auto' }}>
                                                        <table className="custome-table" style={{
                                                            borderCollapse: 'collapse',
                                                            border: '1px solid #ddd',
                                                            width: '100%',
                                                            minWidth: '800px'
                                                        }}>
                                                            <thead>
                                                                {errorMessage ? (
                                                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                                        <th colSpan={4} style={{
                                                                            padding: '40px 20px',
                                                                            textAlign: 'center',
                                                                            border: '1px solid #ddd',
                                                                            color: errorMessage.isSuccess ? '#2e7d32' : '#d32f2f',
                                                                            fontSize: '1em',
                                                                            fontWeight: '600',
                                                                            textTransform: 'none'
                                                                        }}>
                                                                            {typeof errorMessage === 'object' ? errorMessage.text : errorMessage}
                                                                        </th>
                                                                    </tr>
                                                                ) : (
                                                                    <>
                                                                        {/* First row: Entity info - CHANGED data[0] to entity */}
                                                                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                                            <th
                                                                                colSpan={(3 * entityMonthlyFilings.length) + entityLeftColumnsCount}
                                                                                style={{
                                                                                    padding: '14px',
                                                                                    border: '1px solid #ddd',
                                                                                    textAlign: 'left',
                                                                                    fontSize: '14px',
                                                                                    fontWeight: '600',
                                                                                    letterSpacing: '0.5px'
                                                                                }}
                                                                            >
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    justifyContent: 'space-between',
                                                                                    width: '100%',
                                                                                    gap: '20px'
                                                                                }}>
                                                                                    <span style={{ whiteSpace: 'nowrap' }}>
                                                                                        <span style={{ fontWeight: '700' }}>LEGAL NAME:</span> {entity.legal_name || "—"}
                                                                                    </span>
                                                                                    <span style={{ whiteSpace: 'nowrap' }}>
                                                                                        <span style={{ fontWeight: '700' }}>PAN STATUS:</span> {entity.pan_status || "—"}
                                                                                    </span>
                                                                                    <span style={{ whiteSpace: 'nowrap' }}>
                                                                                        <span style={{ fontWeight: '700' }}>GSTN STATUS:</span> {entity.gstn_status || "—"}
                                                                                    </span>
                                                                                    <span style={{ whiteSpace: 'nowrap' }}>
                                                                                        <span style={{ fontWeight: '700' }}>CIN STATUS:</span> {entity.cin_status || "—"}
                                                                                    </span>
                                                                                    <span style={{ whiteSpace: 'nowrap' }}>
                                                                                        <span style={{ fontWeight: '700' }}>MSME REGISTERED YEAR:</span> {entity.msme_details || "-"}
                                                                                    </span>
                                                                                </div>
                                                                            </th>
                                                                        </tr>

                                                                        {/* Second row: Return Types - CHANGED to entityMonthlyFilings and entityYearlyFilings */}
                                                                        <tr style={{ backgroundColor: '#e8e8e8' }}>
                                                                            <th style={{
                                                                                padding: '14px',
                                                                                border: '1px solid #ddd',
                                                                                width: '180px',
                                                                                fontWeight: '600',
                                                                                textAlign: 'center',
                                                                                fontSize: '14px'
                                                                            }}>
                                                                                GSTIN
                                                                            </th>
                                                                            {entityMonthlyFilings.map((row, i) => (
                                                                                <th
                                                                                    key={i}
                                                                                    colSpan={3}
                                                                                    style={{
                                                                                        padding: '14px',
                                                                                        border: '1px solid #ddd',
                                                                                        textAlign: 'center',
                                                                                        fontWeight: '600',
                                                                                        fontSize: '14px'
                                                                                    }}
                                                                                >
                                                                                    {row.Return_Type}
                                                                                </th>
                                                                            ))}
                                                                            {entityYearlyFilings.map((row, i) => (
                                                                                <th
                                                                                    key={`yearly-header-${i}`}
                                                                                    style={{
                                                                                        padding: '14px',
                                                                                        border: '1px solid #ddd',
                                                                                        width: '180px',
                                                                                        fontWeight: '600',
                                                                                        textAlign: 'center',
                                                                                        fontSize: '14px',
                                                                                    }}
                                                                                >
                                                                                    {row.Return_Type}
                                                                                </th>
                                                                            ))}
                                                                        </tr>
                                                                    </>
                                                                )}

                                                            </thead>

                                                            <tbody>
                                                                {/* CHANGED numRows to entityNumRows and data[0] to entity */}
                                                                {errorMessage && !errorMessage.isSuccess ? (
                                                                    null
                                                                ) : (
                                                                    Array.from({ length: entityNumRows }).map((_, rowIndex) => (
                                                                        <tr key={rowIndex}>
                                                                            {/* GSTIN cell */}
                                                                            {rowIndex === 0 && (
                                                                                <td
                                                                                    rowSpan={entityNumRows}
                                                                                    style={{
                                                                                        padding: '14px',
                                                                                        border: '1px solid #ddd',
                                                                                        fontWeight: '600',
                                                                                        textAlign: 'center',
                                                                                        verticalAlign: 'middle',
                                                                                        backgroundColor: '#fafafa',
                                                                                        fontSize: '14px'
                                                                                    }}
                                                                                >
                                                                                    {entity.gstin || "—"}
                                                                                </td>
                                                                            )}

                                                                            {/* For each Return_Type, show 3 months - CHANGED to entityMonthlyFilings */}
                                                                            {entityMonthlyFilings.map((row, i) => {
                                                                                const monthsForThisReturn = Object.keys(row).filter(key => key !== "Return_Type");
                                                                                const startIdx = rowIndex * 3;
                                                                                const monthsInThisRow = monthsForThisReturn.slice(startIdx, startIdx + 3);
                                                                                const cellsToRender = 3;

                                                                                return Array.from({ length: cellsToRender }).map((_, cellIdx) => {
                                                                                    const month = monthsInThisRow[cellIdx];

                                                                                    if (!month) {
                                                                                        return (
                                                                                            <td
                                                                                                key={`${i}-empty-${cellIdx}`}
                                                                                                style={{
                                                                                                    padding: '12px 16px',
                                                                                                    border: '1px solid #ddd',
                                                                                                    minWidth: '150px',
                                                                                                    backgroundColor: rowIndex % 2 === 0 ? '#fafafa' : '#fff'
                                                                                                }}
                                                                                            >
                                                                                                <div style={{ textAlign: 'center', color: '#999' }}>—</div>
                                                                                            </td>
                                                                                        );
                                                                                    }

                                                                                    const statusObj = row[month];
                                                                                    const status = statusObj?.date || "—";
                                                                                    const isFiled = status !== "—" && status.trim() !== "";
                                                                                    const isDelayed = statusObj?.isDelayed || false;

                                                                                    return (
                                                                                        <td
                                                                                            key={`${i}-${month}`}
                                                                                            style={{
                                                                                                padding: '12px 16px',
                                                                                                border: '1px solid #ddd',
                                                                                                minWidth: '150px',
                                                                                                backgroundColor: rowIndex % 2 === 0 ? '#fafafa' : '#fff'
                                                                                            }}
                                                                                        >
                                                                                            <div style={{
                                                                                                fontSize: '14px',
                                                                                                fontWeight: '600',
                                                                                                color: '#666',
                                                                                                textAlign: 'center',
                                                                                                textTransform: 'uppercase',
                                                                                                letterSpacing: '0.5px',
                                                                                                marginBottom: '8px'
                                                                                            }}>
                                                                                                {month}
                                                                                            </div>

                                                                                            <div style={{
                                                                                                fontSize: '14px',
                                                                                                fontWeight: 'Bold',
                                                                                                textAlign: 'center',
                                                                                                padding: '4px 12px',
                                                                                                borderRadius: '4px',
                                                                                                display: 'inline-block',
                                                                                                backgroundColor: isFiled ? (isDelayed ? '#ffebee' : '#e8f5e9') : '#ffebee',
                                                                                                color: isFiled ? (isDelayed ? '#d32f2f' : '#2e7d32') : '#d32f2f',
                                                                                            }}>
                                                                                                {status}
                                                                                            </div>
                                                                                        </td>
                                                                                    );
                                                                                });
                                                                            })}

                                                                            {/* Yearly filings - CHANGED to entityYearlyFilings and entityNumRows */}
                                                                            {rowIndex === 0 && entityYearlyFilings.map((yearlyRow, yIdx) => {
                                                                                const allMonths = Object.keys(yearlyRow).filter(k => k !== "Return_Type");
                                                                                const filingInfo = allMonths.map(month => {
                                                                                    const statusObj = yearlyRow[month];
                                                                                    const status = statusObj?.date;
                                                                                    const isDelayed = statusObj?.isDelayed;
                                                                                    return status && status !== "—" && status !== "-" && status.trim() !== "" ? { date: status, isDelayed } : null;
                                                                                }).filter(Boolean);

                                                                                const displayStatus = filingInfo.map(info => info.date).join(", ") || "—";
                                                                                const hasAnyDelayed = filingInfo.some(info => info.isDelayed);
                                                                                const isFiled = displayStatus !== "—";

                                                                                return (
                                                                                    <td
                                                                                        key={`yearly-${yIdx}`}
                                                                                        rowSpan={entityNumRows}
                                                                                        style={{
                                                                                            padding: '14px',
                                                                                            border: '1px solid #ddd',
                                                                                            fontWeight: 'Bold',
                                                                                            textAlign: 'center',
                                                                                            verticalAlign: 'middle',
                                                                                            fontSize: '14px',
                                                                                        }}
                                                                                    >
                                                                                        <span style={{
                                                                                            padding: '4px 12px',
                                                                                            borderRadius: '4px',
                                                                                            display: 'inline-block',
                                                                                            backgroundColor: isFiled ? (hasAnyDelayed ? '#ffebee' : '#e8f5e9') : '#ffebee',
                                                                                            color: isFiled ? (hasAnyDelayed ? '#d32f2f' : '#2e7d32') : '#d32f2f',
                                                                                        }}>
                                                                                            {displayStatus}
                                                                                        </span>
                                                                                    </td>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    ))
                                                                )}


                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </BoxWrapper>

                                                {/* Penalty Exposure button - CHANGED to pass entity.id and entity.seller_code */}
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4, pr: { xs: 2, md: 0 } }}>
                                                    <button
                                                        className="button-primary"
                                                        onClick={() => {
                                                            // Extract GST returns ONLY for THIS specific entity
                                                            const entitySpecificReturns = entity.filing_data
                                                                ? extractAvailableGSTReturns(entity.filing_data)
                                                                : [];

                                                            // Store entity-specific returns
                                                            if (entitySpecificReturns.length > 0) {
                                                                setAvailableGSTReturns(entitySpecificReturns);
                                                                localStorage.setItem('available_gst_returns', JSON.stringify(entitySpecificReturns));
                                                            }

                                                            localStorage.setItem("sCode", entity.seller_code);
                                                            localStorage.setItem("sId", entity.id);
                                                            setShowPenaltyView(true);
                                                            handlePenaltyCalculation(
                                                                entitySpecificReturns.length > 0 ? entitySpecificReturns[0] : 'GSTR1',
                                                                entity.id,
                                                                entity.seller_code
                                                            );
                                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                                        }}
                                                        disabled={isPenaltyButtonDisabled}
                                                        style={{
                                                            padding: "8px 15px",
                                                            opacity: isPenaltyButtonDisabled ? 0.5 : 1,
                                                            cursor: isPenaltyButtonDisabled ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        Penalty Exposure
                                                    </button>
                                                </Box>
                                            </React.Fragment>
                                        );
                                    })
                                ) : remarks && remarks.length > 0 && isOnlyMain === false ? (
                                    // Show remarks only if no filing data available
                                    remarks.map((entity, index) => (
                                        <BoxWrapper
                                            boxTitle={
                                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                    <Typography
                                                        variant="h6"
                                                        component="div"
                                                        sx={{
                                                            padding: '4px 20px 4px 0px',
                                                            fontSize: '18px',
                                                            fontFamily: 'Dosis',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Filing Status
                                                    </Typography>
                                                    {lastReportRunDatetime && (
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '4px 20px 4px 0px'
                                                        }}>
                                                            <p style={{
                                                                marginRight: '10px',
                                                                marginBottom: '0',
                                                                fontSize: '18px',
                                                                fontWeight: 500,
                                                                color: 'black',
                                                                fontFamily: 'Dosis'
                                                            }}>
                                                                Last Report Run Date Time:
                                                            </p>
                                                            <Chip
                                                                label={lastReportRunDatetime}
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    height: '32px',
                                                                    '& .MuiChip-label': {
                                                                        padding: '0 14px'
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </Box>
                                            }
                                            enableAddButton={false}
                                        >
                                            <div style={{ overflowX: 'auto' }}>
                                                <table className="custome-table" style={{
                                                    borderCollapse: 'collapse',
                                                    border: '1px solid #ddd',
                                                    width: '100%',
                                                    minWidth: '800px'
                                                }}>
                                                    <tbody>
                                                        <tr>
                                                            <td style={{
                                                                padding: '20px',
                                                                textAlign: 'center',
                                                                border: '1px solid #ddd',
                                                                color: entity.status === 'Failed' ? '#d32f2f' : '#ff9800',
                                                                fontSize: '1em',
                                                                fontWeight: '600'
                                                            }}>
                                                                {entity.remarks}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </BoxWrapper>
                                    ))
                                ) : errorMessage ? (
                                    <BoxWrapper
                                        boxTitle={
                                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                    sx={{
                                                        padding: '4px 20px 4px 0px',
                                                        fontSize: '18px',
                                                        fontFamily: 'Dosis',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    Filing Status
                                                </Typography>
                                                {lastReportRunDatetime && (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '4px 20px 4px 0px'
                                                    }}>
                                                        <p style={{
                                                            marginRight: '10px',
                                                            marginBottom: '0',
                                                            fontSize: '18px',
                                                            fontWeight: 500,
                                                            color: 'black',
                                                            fontFamily: 'Dosis'
                                                        }}>
                                                            Last Report Run Date Time:
                                                        </p>
                                                        <Chip
                                                            label={lastReportRunDatetime}
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            sx={{
                                                                fontSize: '14px',
                                                                height: '32px',
                                                                '& .MuiChip-label': {
                                                                    padding: '0 14px'
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </Box>
                                        }
                                        enableAddButton={false}
                                    >
                                        <div style={{ overflowX: 'auto' }}>
                                            <table className="custome-table" style={{
                                                borderCollapse: 'collapse',
                                                border: '1px solid #ddd',
                                                width: '100%',
                                                minWidth: '800px'
                                            }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                        <th colSpan={4} style={{
                                                            padding: '40px 20px',
                                                            textAlign: 'center',
                                                            border: '1px solid #ddd',
                                                            color: errorMessage.isSuccess ? '#2e7d32' : '#d32f2f',
                                                            fontSize: '1em',
                                                            fontWeight: '600',
                                                            textTransform: 'none'
                                                        }}>
                                                            {typeof errorMessage === 'object' ? errorMessage.text : errorMessage}
                                                        </th>
                                                    </tr>
                                                </thead>
                                            </table>
                                        </div>
                                    </BoxWrapper>
                                ) : null}
                                {failedEntities.length > 0 && failedEntities.map((entity, index) => (
                                    <BoxWrapper
                                        boxTitle={
                                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                    sx={{
                                                        padding: '4px 20px 4px 0px',
                                                        fontSize: '18px',
                                                        fontFamily: 'Dosis',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    Filing Status
                                                </Typography>
                                                {lastReportRunDatetime && (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '4px 20px 4px 0px'
                                                    }}>
                                                        <p style={{
                                                            marginRight: '10px',
                                                            marginBottom: '0',
                                                            fontSize: '18px',
                                                            fontWeight: 500,
                                                            color: 'black',
                                                            fontFamily: 'Dosis'
                                                        }}>
                                                            Last Report Run Date Time:
                                                        </p>
                                                        <Chip
                                                            label={lastReportRunDatetime}
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            sx={{
                                                                fontSize: '14px',
                                                                height: '32px',
                                                                '& .MuiChip-label': {
                                                                    padding: '0 14px'
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </Box>
                                        }
                                        enableAddButton={false}
                                    >

                                        <div style={{ overflowX: 'auto' }}>
                                            <table className="custome-table" style={{
                                                borderCollapse: 'collapse',
                                                border: '1px solid #ddd',
                                                width: '100%',
                                                minWidth: '800px'
                                            }}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{
                                                            padding: '20px',
                                                            textAlign: 'center',
                                                            border: '1px solid #ddd',
                                                            color: '#d32f2f',
                                                            fontSize: '1em',
                                                            fontWeight: '600'
                                                        }}>
                                                            {entity.gstin}: {entity.remarks}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </BoxWrapper>
                                ))}
                            </Grid>


                        </Grid>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
                    <Box sx={{ flex: 1, p: { xs: 1.5, md: 0 } }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2, px: { xs: 1, md: 2 } }}>
                            <button
                                className="button-outlined-primary"
                                onClick={() => {
                                    setShowPenaltyView(false);
                                    setShowPenaltyTable(false);
                                    setPenaltyData(null);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                style={{ padding: "8px 15px" }}
                            >
                                Back
                            </button>
                        </Box>
                        <Grid container spacing={3} sx={{ px: { xs: 1, md: 0 }, justifyContent: 'center' }}>
                            <Box sx={{ px: { xs: 1, md: 2 }, mt: 3, width: '100%' }}>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-around",
                                        alignItems: "center",
                                        backgroundColor: "rgb(243, 245, 248)",
                                        padding: "8px",
                                        borderRadius: "25px",
                                        margin: "10px auto",
                                        maxWidth: "1400px",
                                        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                                        border: "1px solid #d1d5db",
                                    }}
                                >
                                    {availableGSTReturns.map((returnType, index) => {
                                        const colorSchemes = {
                                            'GSTR1': { gradient: 'linear-gradient(135deg, #ffd3a5 0%, #fd9853 50%, #e8a87c 100%)', icon: 'fas fa-file-invoice', iconColor: '#fd9853', shadow: 'rgba(255, 211, 165, 0.4)' },
                                            'GSTR3B': { gradient: 'linear-gradient(135deg, #a8e6cf 0%, #56c596 50%, #88d8b0 100%)', icon: 'fas fa-calculator', iconColor: '#56c596', shadow: 'rgba(168, 230, 207, 0.4)' },
                                            'GSTR9': { gradient: 'linear-gradient(135deg, #ffc3a0 0%, #ff677d 50%, #ff8fab 100%)', icon: 'fas fa-chart-bar', iconColor: '#ff677d', shadow: 'rgba(255, 195, 160, 0.4)' },
                                            'GSTR9C': { gradient: 'linear-gradient(135deg, #a8c0ff 0%, #667eea 50%, #8b9ad6 100%)', icon: 'fas fa-file-contract', iconColor: '#667eea', shadow: 'rgba(168, 192, 255, 0.4)' }
                                        };

                                        const scheme = colorSchemes[returnType] || colorSchemes['GSTR1'];
                                        const isActive = selectedPenaltyCard === returnType || (showPenaltyTable && penaltyData?.file_type === returnType);
                                        const sCode = localStorage.getItem("sCode");
                                        const sId = localStorage.getItem("sId");
                                        return (
                                            <button
                                                key={returnType}
                                                onClick={() => handlePenaltyCalculation(returnType, sId, sCode)}
                                                disabled={penaltyLoading && selectedPenaltyCard === returnType}
                                                style={{
                                                    flex: 1,
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    position: "relative",
                                                    padding: "14px 24px",
                                                    color: isActive ? "#fff" : "#333",
                                                    fontSize: "15px",
                                                    fontWeight: "600",
                                                    background: isActive ? scheme.gradient : "transparent",
                                                    border: "none",
                                                    borderRadius: "20px",
                                                    cursor: penaltyLoading ? "not-allowed" : "pointer",
                                                    transition: "all 0.3s",
                                                    opacity: penaltyLoading && selectedPenaltyCard === returnType ? 0.6 : 1,
                                                    boxShadow: isActive ? `0 4px 12px ${scheme.shadow}` : "none",
                                                    margin: "0 6px",
                                                }}
                                            >
                                                <i
                                                    className={scheme.icon}
                                                    style={{
                                                        fontSize: "22px",
                                                        marginRight: isActive ? "8px" : "0",
                                                        color: isActive ? "#fff" : scheme.iconColor
                                                    }}
                                                ></i>
                                                <span style={{ color: isActive ? "#fff" : "#333" }}>
                                                    {penaltyLoading && selectedPenaltyCard === returnType ? 'Loading...' : returnType}
                                                </span>
                                                {isActive && (
                                                    <span style={{
                                                        position: "absolute",
                                                        top: "8px",
                                                        right: "8px",
                                                        width: "10px",
                                                        height: "10px",
                                                        backgroundColor: "#fff",
                                                        borderRadius: "50%",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                    }}></span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Box>
                            {showPenaltyTable && penaltyData && (
                                <Grid item xs={isOnlyMain === false ? 11.5 : 12} sx={{ mt: 0 }}>
                                    <BoxWrapper
                                        boxTitle={
                                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                    sx={{
                                                        padding: '4px 20px 4px 0px',
                                                        fontSize: '18px',
                                                        fontFamily: 'Dosis',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {`Penalty Details - ${penaltyData.file_type}`}
                                                </Typography>
                                                {(penaltyData.file_type === 'GSTR3B' || penaltyData.file_type === 'GSTR9') && (
                                                    <button
                                                        className="button-primary"
                                                        onClick={handleSave}
                                                        disabled={saveLoading}
                                                        style={{
                                                            padding: "6px 12px",
                                                            opacity: saveLoading ? 0.6 : 1,
                                                            cursor: saveLoading ? 'not-allowed' : 'pointer',
                                                            height: '32px',
                                                            fontSize: '14px',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        {saveLoading ? 'Saving...' : 'Save'}
                                                    </button>
                                                )}
                                            </Box>
                                        }
                                    >
                                        <div style={{ overflowX: 'auto' }}>
                                            <table className="custome-table" style={{
                                                borderCollapse: 'collapse',
                                                border: '1px solid #ddd',
                                                width: '100%',
                                                minWidth: '800px'
                                            }}>
                                                {penaltyErrorMessage ? (
                                                    // Error/Message display in table
                                                    <>

                                                    <tbody>
                                                        <tr style={{ backgroundColor: '#fff' }}>
                                                            <td style={{
                                                                padding: '40px 20px',
                                                                border: '1px solid #ddd',
                                                                textAlign: 'center',
                                                                color: '#d32f2f',
                                                                fontSize: '1em',
                                                                fontWeight: '600'
                                                            }}>
                                                                {penaltyErrorMessage}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                    </>
                                                ) : penaltyData.penalty_details && penaltyData.penalty_details.length > 0 ? (
                                                    // Existing table structure with data
                                                    <>
                                                        <thead>
                                                            <tr style={{ backgroundColor: '#B8B8DC' }}>
                                                                {penaltyData.file_type === 'GSTR1' ? (
                                                                    <>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Filing Period</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Due Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Filed Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Delay Period</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Penalty FOR EACH Day OF DEFAULT</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Total Penalty FOR DELAYS</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Maximum Late Fees</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Total Liability</th>
                                                                    </>
                                                                ) : penaltyData.file_type === 'GSTR3B' ? (
                                                                    <>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Filing Period</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Due Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Filed Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Delay Period</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Tax Paid</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Penalty FOR EACH Day OF DEFAULT</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Total Penalty FOR DELAYS</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Interest Rate Per Anum</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Interest Amount</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Penalty Exposure</th>
                                                                    </>
                                                                ) : penaltyData.file_type === 'GSTR9' ? (
                                                                    <>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Turnover Slab</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Due Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Filed Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Delay Period</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Turnover</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Penalty FOR EACH Day OF DEFAULT</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Total Penalty FOR DELAYS</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Maximum Late Fees</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Penalty Exposure</th>
                                                                    </>
                                                                ) : penaltyData.file_type === 'GSTR9C' ? (
                                                                    <>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Filing Period</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Due Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Filed Date</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Delay Period</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Penalty FOR EACH Day OF DEFAULT</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Total Penalty FOR DELAYS</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Maximum Late Fees</th>
                                                                        <th style={{ padding: '14px', border: '1px solid #ddd', fontWeight: 'bold', color: '#3b3b6b', textAlign: 'center', fontSize: '14px' }}>Penalty Exposure</th>
                                                                    </>
                                                                ) : null}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {penaltyData.penalty_details.map((row, index) => (
                                                                <tr
                                                                    key={index}
                                                                    style={{
                                                                        backgroundColor: index % 2 === 0 ? '#F0F0F8' : '#fff',
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E8E8F5'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#F0F0F8' : '#fff'}
                                                                >
                                                                    {penaltyData.file_type === 'GSTR1' ? (
                                                                        <>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.filing_period}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.due_date}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.filing_date}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.delay_period}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.penalty_for_each_day_of_default}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.total_penalty_for_delays}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.maximum_late_fees}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.penalty_exposure}</td>
                                                                        </>
                                                                    ) : penaltyData.file_type === 'GSTR3B' ? (
                                                                        <>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.filing_period}</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.due_date}</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.filing_date}</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.delay_period}</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    step="0.01"
                                                                                    value={editableTaxPaid[index] !== undefined ? editableTaxPaid[index] : row.tax_paid}
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value;
                                                                                        // Allow empty string or valid numbers with up to 2 decimal places
                                                                                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                                                            handleTaxPaidChange(index, value);
                                                                                        }
                                                                                    }}
                                                                                    onBlur={(e) => {
                                                                                        if (parseInt(row.delay_period) > 0) {
                                                                                            let value = e.target.value;
                                                                                            if (value === '') {
                                                                                                handleTaxPaidChange(index, '0');
                                                                                            } else {
                                                                                                // Format to 2 decimal places on blur
                                                                                                const numValue = parseFloat(value);
                                                                                                if (!isNaN(numValue)) {
                                                                                                    handleTaxPaidChange(index, numValue.toFixed(2));
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                        e.target.style.borderColor = '#ddd';
                                                                                    }}
                                                                                    disabled={parseInt(row.delay_period) === 0}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                                                                            e.preventDefault();
                                                                                        }
                                                                                    }}
                                                                                    onFocus={(e) => {
                                                                                        if (parseInt(row.delay_period) > 0) {
                                                                                            e.target.style.borderColor = '#1976d2';
                                                                                            if (e.target.value === '0') {
                                                                                                e.target.select();
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        padding: '2px 8px',
                                                                                        border: '1px solid #ddd',
                                                                                        borderRadius: '4px',
                                                                                        textAlign: 'center',
                                                                                        fontSize: '14px',
                                                                                        outline: 'none',
                                                                                        backgroundColor: parseInt(row.delay_period) === 0 ? '#f5f5f5' : '#fff',
                                                                                        cursor: parseInt(row.delay_period) === 0 ? 'not-allowed' : 'text',
                                                                                        opacity: parseInt(row.delay_period) === 0 ? 0.6 : 1
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.penalty_for_each_day_of_default}</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.total_penalty_for_delays}</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.interest_rate_pa}%</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                ₹{calculatedValues[index]?.interestAmount || row.interest_amount}
                                                                            </td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                ₹{calculatedValues[index]?.penaltyExposure || row.penalty_exposure}
                                                                            </td>
                                                                        </>
                                                                    ) : penaltyData.file_type === 'GSTR9' ? (
                                                                        <>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.turnover?.toLocaleString('en-IN') || 0}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.due_date}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.filing_date}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.delay_period}</td>
                                                                            <td style={{ padding: '8px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    step="0.01"
                                                                                    value={editableTurnover[index] !== undefined ? editableTurnover[index] : row.turnover}
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value;
                                                                                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                                                            handleTurnoverChange(index, value);
                                                                                        }
                                                                                    }}
                                                                                    onBlur={(e) => {
                                                                                        if (parseInt(row.delay_period) > 0) {
                                                                                            let value = e.target.value;
                                                                                            if (value === '') {
                                                                                                handleTurnoverChange(index, '0');
                                                                                            } else {
                                                                                                const numValue = parseFloat(value);
                                                                                                if (!isNaN(numValue)) {
                                                                                                    handleTurnoverChange(index, numValue.toFixed(2));
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                        e.target.style.borderColor = '#ddd';
                                                                                    }}
                                                                                    disabled={parseInt(row.delay_period) === 0}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                                                                            e.preventDefault();
                                                                                        }
                                                                                    }}
                                                                                    onFocus={(e) => {
                                                                                        if (parseInt(row.delay_period) > 0) {
                                                                                            e.target.style.borderColor = '#1976d2';
                                                                                            if (e.target.value === '0') {
                                                                                                e.target.select();
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        padding: '2px 8px',
                                                                                        border: '1px solid #ddd',
                                                                                        borderRadius: '4px',
                                                                                        textAlign: 'center',
                                                                                        fontSize: '14px',
                                                                                        outline: 'none',
                                                                                        backgroundColor: parseInt(row.delay_period) === 0 ? '#f5f5f5' : '#fff',
                                                                                        cursor: parseInt(row.delay_period) === 0 ? 'not-allowed' : 'text',
                                                                                        opacity: parseInt(row.delay_period) === 0 ? 0.6 : 1
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                ₹{calculatedValues[`gstr9_${index}`]?.penaltyPerDay || row.penalty_for_each_day_of_default}
                                                                            </td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                ₹{calculatedValues[`gstr9_${index}`]?.totalPenalty || row.total_penalty_for_delays}
                                                                            </td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                ₹{calculatedValues[`gstr9_${index}`]?.maximumLateFees || row.maximum_late_fees}
                                                                            </td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>
                                                                                ₹{calculatedValues[`gstr9_${index}`]?.penaltyExposure || row.penalty_exposure}
                                                                            </td>
                                                                        </>
                                                                    ) : penaltyData.file_type === 'GSTR9C' ? (
                                                                        <>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.filing_period}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.due_date}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.filing_date}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>{row.delay_period}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.penalty_for_each_day_of_default}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.total_penalty_for_delays}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.maximum_late_fees}</td>
                                                                            <td style={{ padding: '12px 16px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}>₹{row.penalty_exposure}</td>
                                                                        </>
                                                                    ) : null}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </>
                                                ) : null}
                                            </table>
                                        </div>
                                    </BoxWrapper>
                                </Grid>
                            )}
                        </Grid>

                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ComplianceMonitor;


